/**
 * Sawyer 11 client-notes-agreements (F1) — shared, pure display helpers for
 * client notes (the panel, the desk digest, the dog page). P5: never invent
 * an author, a pinner or a date we do not have.
 */
import type { CustomerNote } from '../../lib/api.js';

/** "Oct 24, 2026" — raw ISO strings are an anti-pattern in this app. */
export function formatNoteDate(iso: string | null | undefined): string {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Author line: imported notes say so; a kept booking note says whose choice
 * it was (the words are the booking's, not the staffer's); an unresolved
 * author is "Unknown staff".
 */
export function noteAuthor(note: Pick<CustomerNote, 'source' | 'author_name'>): string {
  if (note.source === 'gingr_import') return 'Imported from Gingr';
  if (note.source === 'booking_note') {
    return `From a booking note · added by ${note.author_name ?? 'Unknown staff'}`;
  }
  return note.author_name ?? 'Unknown staff';
}

/**
 * "Pinned by Priya Desk · Oct 24, 2026", or plain "Pinned" for a pin that
 * predates attribution (pinned_at null). Null when the note is not pinned.
 */
export function pinAttribution(
  note: Pick<CustomerNote, 'pinned' | 'pinned_at' | 'pinned_by_name'>,
): string | null {
  if (!note.pinned) return null;
  if (!note.pinned_at) return 'Pinned';
  const who = note.pinned_by_name ?? 'Unknown staff';
  return `Pinned by ${who} · ${formatNoteDate(note.pinned_at)}`;
}

/** Pinned group order: newest pin first, legacy (unattributed) pins last. */
export function comparePinned(a: CustomerNote, b: CustomerNote): number {
  const pa = a.pinned_at ? Date.parse(a.pinned_at) : -Infinity;
  const pb = b.pinned_at ? Date.parse(b.pinned_at) : -Infinity;
  if (pa !== pb) return pb - pa;
  return Date.parse(b.created_at) - Date.parse(a.created_at);
}

/** Newest first. */
export function compareNewest(a: CustomerNote, b: CustomerNote): number {
  return Date.parse(b.created_at) - Date.parse(a.created_at);
}

/** A body long enough that the 3-line clamp needs a "Show more" control. */
export const NOTE_CLAMP_CHARS = 160;
